import React, { useState, useEffect, useRef } from "react";
import { useApp } from "context";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  SelectChangeEvent,
} from "@mui/material";
import { Helpers } from "utils";
import { PairValue } from "types";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

type Props = {
  sx?: any;
  id?: string;
  name?: string;
  label?: string;
  required?: boolean;
  items: PairValue[];
  disabled?: boolean;
  clipboard?: boolean;
  value?: number | "";
  variant?: "filled" | "outlined";
  onChange?: (value: number) => void;
  onDropdownChange?: (event: SelectChangeEvent<number>) => void;
};

export const Select = (props: Props) => {
  const { theme } = useApp();

  const [value, setValue] = useState<number | "">(props.value ?? "");

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    setValue(props.value ?? "");
  }, [props.value]);

  // TODO: not exactly sure why I had to use this. Anyway, I chose -1 to clear Select
  useEffect(() => {
    value === -1 && setValue("");
  }, [value]);

  const variant = Helpers.hasValue(props.label) ? "filled" : "outlined";

  const isClipboard = props.clipboard ?? false;

  const handleChange = (value: number): void => {
    setValue(value);
    props.onChange && props.onChange(value);
  };

  const handleDropdownChange = (event: SelectChangeEvent<number>): void =>
    props.onDropdownChange && props.onDropdownChange(event);

  const handleClipboard = (): void => {
    const item = props.items.find((item: PairValue) => item.key === value);
    if (item) {
      navigator.clipboard.writeText(item.value);
    }
  };

  return (
    <>
      {props.items ? (
        <FormControl
          variant={props.variant || variant}
          sx={{ width: "100%", display: "flex", flexDirection: "row" }}
        >
          {props.label ? (
            <InputLabel
              id={props.id}
              sx={{
                color: props.disabled
                  ? theme.palette.background.accent
                  : theme.palette.font.color,
              }}
            >
              {props.label}
            </InputLabel>
          ) : null}
          <MUISelect
            name={props.name}
            labelId={props.id}
            value={value}
            required={props.required}
            disabled={props.disabled}
            onChange={handleDropdownChange}
            sx={{
              width: "100%",
              "& .MuiSelect-icon": {
                color: props.disabled
                  ? theme.palette.background.accent
                  : theme.palette.font.color,
              },
              "& .MuiFormLabel-root": {
                color: theme.palette.font.color,
              },
              "& .MuiSelect-select": {
                color: theme.palette.font.color,
              },
              "& .MuiSelect-filled": {
                borderRadius: theme.border.radius / 4,
                backgroundColor: theme.palette.background.color,
                borderBottomWidth: 2,
                borderBottomStyle: "solid",
                borderBottomColor: theme.palette.background.color,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                "&:focus": {
                  borderBottomColor: theme.color.primary.color,
                },
              },
            }}
            MenuProps={{
              MenuListProps: {
                sx: {
                  color: theme.palette.font.color,
                  backgroundColor: theme.palette.background.accent,
                },
              },
            }}
          >
            {props.items.map((item: PairValue) => (
              <MenuItem
                key={item.key}
                value={item.key}
                onClick={() => handleChange(item.key)}
              >
                {item.value}
              </MenuItem>
            ))}
          </MUISelect>
          {isClipboard ? (
            <IconButton
              tabIndex={-1}
              onClick={handleClipboard}
              sx={{
                position: "absolute",
                color: theme.palette.font.color,
                top: theme.margin.xs * theme.margin.factor,
                right: theme.margin.xl * theme.margin.factor,
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          ) : null}
        </FormControl>
      ) : null}
    </>
  );
};
